import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { ChatSession } from '@/types/mission-control';
import { sessionsKey } from '../constants';
import { callAgentForge } from './http';
import type {
  CreateSessionInput,
  SessionResponse,
  SessionsListResponse,
  UpdateSessionInput,
  UseSessionsArgs,
} from './sessions.types';

const SESSIONS_STALE_TIME_MS = 30_000;

const listSessions = async (
  agentId: string | undefined,
  signal: AbortSignal | undefined
): Promise<ChatSession[]> => {
  const body = await callAgentForge<SessionsListResponse>({
    url: '/sessions',
    params: agentId ? { agent_id: agentId } : undefined,
    signal,
  });
  return body.sessions ?? [];
};

const createSession = async (
  input: CreateSessionInput
): Promise<ChatSession> => {
  const body = await callAgentForge<SessionResponse>({
    method: 'POST',
    url: '/sessions',
    body: input,
  });
  return body.session;
};

const updateSession = async (
  input: UpdateSessionInput
): Promise<ChatSession> => {
  const body = await callAgentForge<SessionResponse>({
    method: 'PUT',
    url: `/sessions/${input.sessionId}`,
    body: { title: input.title },
  });
  return body.session;
};

const deleteSession = async (sessionId: string): Promise<void> => {
  await callAgentForge({
    method: 'DELETE',
    url: `/sessions/${sessionId}`,
  });
};

export const useSessionsQuery = (
  args: UseSessionsArgs = {}
): UseQueryResult<ChatSession[], Error> => {
  return useQuery({
    queryKey: sessionsKey(args.agentId),
    queryFn: ({ signal }) => listSessions(args.agentId, signal),
    staleTime: SESSIONS_STALE_TIME_MS,
    enabled: args.enabled ?? true,
  });
};

export const useCreateSessionMutation = (): UseMutationResult<
  ChatSession,
  Error,
  CreateSessionInput
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSession,
    onSuccess: (_session, variables) => {
      queryClient.invalidateQueries({
        queryKey: sessionsKey(variables.agent_id),
      });
    },
  });
};

export const useUpdateSessionMutation = (): UseMutationResult<
  ChatSession,
  Error,
  UpdateSessionInput
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sessionsKey() });
    },
  });
};

export const useDeleteSessionMutation = (): UseMutationResult<
  void,
  Error,
  string
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sessionsKey() });
    },
  });
};
